import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, BookOpen, Check, CircuitBoard, GitBranch, Package, SlidersHorizontal } from 'lucide-react';
import { products, storeName } from '../data/mock';
import OptimizedImage from './OptimizedImage';
import GateDemo from './GateDemo';
import PcbHeader from './PcbHeader';
import PcbFooter from './PcbFooter';
import ContactSection, { focusContact } from './ContactSection';
import usePageMeta from '../lib/usePageMeta';
import { PAGES } from '../seoMeta.mjs';
import reportBuyConversion from '../lib/reportConversion';
import './landing.css';
import './home.css';

const BOARD_DETAILS = {
  'logic-gates': {
    role: 'Explore individual gates',
    description: 'Make Boolean logic visible. Set the inputs with physical switches and compare the LED output with the truth table beside each gate.',
    image: '/images/logic%20gates/front.webp', width: 1200, height: 900,
    features: [
      'AND, OR, NOT, NAND, NOR, XOR, XNOR and BUFFER on one board',
      'Physical input switches and LEDs show each logic state',
      'Gate symbols and truth tables printed on the board',
      'Powered by 2 × AAA batteries; no software or soldering',
    ],
    note: 'For exploring and demonstrating how individual gates behave.',
  },
  'logic-learner': {
    role: 'Build and investigate circuits',
    description: 'Connect gates with jumper wires, then explore how clocks, counters and memory turn simple logic into working digital circuits.',
    image: '/images/logic%20trainer/front.webp', width: 1500, height: 784,
    features: [
      '16 gates: two of each type, connected with jumper wires',
      'Adjustable 0.2–5 Hz clock, plus manual stepping',
      '4-bit memory, 8-bit shift register, BCD counter and display',
      'Powered by 3 × AAA batteries; no software or soldering',
    ],
    note: 'A standalone board for combinational and sequential logic.',
  },
};

const priceLabel = (product) => product.price.replace(/\.00$/, '');

const tracked = (event, placement, product) => ({
  'data-umami-event': event,
  'data-umami-event-placement': placement,
  ...(product && { 'data-umami-event-product': product.id }),
});

const BuyButton = ({ product, placement, children }) => (
  <a href={product.buyLink} target="_blank" rel="noopener noreferrer"
    className="home-button home-button-primary" onClick={reportBuyConversion}
    {...tracked('shop_click', placement, product)}>
    {children || `Buy on ${storeName}`} <ArrowUpRight size={17} aria-hidden="true" />
    <span className="sr-only"> (opens in a new tab)</span>
  </a>
);

const DetailLink = ({ product, placement, children }) => (
  <Link to={`/product/${product.id}`} onClick={() => window.scrollTo(0, 0)}
    className="home-text-link" {...tracked('product_details', placement, product)}>
    {children || 'Photos, videos & specs'} <ArrowRight size={17} aria-hidden="true" />
  </Link>
);

const LandingPage = () => {
  usePageMeta(PAGES.home);
  const location = useLocation();
  const gates = products.find((product) => product.id === 'logic-gates');
  const trainer = products.find((product) => product.id === 'logic-learner');

  useEffect(() => {
    if (location.hash === '#products' || location.hash === '#contact') {
      document.getElementById(location.hash.slice(1))?.scrollIntoView();
      if (location.hash === '#contact') focusContact();
    }
  }, [location.hash]);

  return (
    <div className="pcb-root home-page">
      <a className="home-skip" href="#main-content">Skip to content</a>
      <PcbHeader />
      <main id="main-content">
        <section className="home-hero home-container" aria-labelledby="home-heading">
          <div className="home-hero-title">
            <p className="home-eyebrow"><span aria-hidden="true" /> Meet Logic Trainer</p>
            <h1 id="home-heading">Build circuits.<br /><span>See logic work.</span></h1>
          </div>
          <figure className="home-hero-figure">
            <div className="home-hero-board-label"><span>{trainer.name}</span><span className="home-stock"><span aria-hidden="true" />{trainer.inStock ? 'In stock' : 'Out of stock'}</span></div>
            <Link to={`/product/${trainer.id}`} aria-label="View Logic Trainer photos and specifications"
              onClick={() => window.scrollTo(0, 0)} {...tracked('product_details', 'hero_photo', trainer)}>
              <OptimizedImage src={BOARD_DETAILS[trainer.id].image}
                alt="Logic Trainer board with sixteen logic gates, input switches, adjustable clock, memory, counter and seven-segment display"
                width={BOARD_DETAILS[trainer.id].width} height={BOARD_DETAILS[trainer.id].height} loading="eager" fetchPriority="high" />
              <span className="home-hero-photo-link" aria-hidden="true"><ArrowUpRight size={20} /></span>
            </Link>
            <figcaption className="home-hero-specs">
              <div><strong>16</strong><span>Connectable gates</span></div>
              <div><strong>0.2–5 <small>Hz</small></strong><span>Clock + manual step</span></div>
              <div><strong>4-bit</strong><span>Built-in memory</span></div>
            </figcaption>
          </figure>
          <div className="home-hero-details">
            <p className="home-intro">Your hands-on digital logic lab. Connect gates with jumper wires, follow the signals, and bring clocks, counters and memory to life.</p>
            <p className="home-hero-description">No software. No soldering. Just real circuits.<br />Powered by 3 × AAA batteries.</p>
            <div className="home-actions">
              <BuyButton product={trainer} placement="hero">Buy Logic Trainer · {priceLabel(trainer)}</BuyButton>
              <DetailLink product={trainer} placement="hero">Explore the board</DetailLink>
            </div>
            <p className="home-purchase-note">Price in USD · Checkout on {storeName}<br />Ships worldwide from Estonia</p>
            <a href="#products" className="home-text-link home-hero-compare" {...tracked('compare_boards', 'hero')}>Compare both learning boards <ArrowRight size={15} aria-hidden="true" /></a>
          </div>
        </section>

        <div className="home-proof-strip">
          <div className="home-container home-proof-grid">
            <a href="https://www.youtube.com/watch?v=CktljgyKILE&t=1035s" target="_blank" rel="noopener noreferrer">
              <CircuitBoard size={22} aria-hidden="true" /><span><strong>Logic Gates on EEVblog</strong><span>Watch the original board at 17:15 <ArrowUpRight size={13} aria-hidden="true" /></span></span>
            </a>
            <a href="https://certification.oshwa.org/ee000005.html" target="_blank" rel="noopener noreferrer">
              <GitBranch size={22} aria-hidden="true" /><span><strong>Open-source Logic Gates</strong><span>OSHWA certified · EE000005 <ArrowUpRight size={13} aria-hidden="true" /></span></span>
            </a>
            <Link to="/logic-wiki" onClick={() => window.scrollTo(0, 0)} {...tracked('wiki_click', 'proof_strip')}>
              <BookOpen size={22} aria-hidden="true" /><span><strong>Free companion learning material</strong><span>Gate explanations and hands-on labs <ArrowRight size={13} aria-hidden="true" /></span></span>
            </Link>
          </div>
        </div>

        <section id="products" className="home-section home-container" aria-labelledby="products-heading">
          <div className="home-section-heading">
            <div><p className="home-eyebrow">Find your board</p><h2 id="products-heading">Start curious. Keep building.</h2></div>
            <p>Build complete circuits with Logic Trainer,<br className="home-desktop-break" /> or explore the fundamentals with Logic Gates.</p>
          </div>
          <div className="home-products">
            {[trainer, gates].map((product) => {
              const board = BOARD_DETAILS[product.id];
              return (
                <article className={`home-product${product.id === trainer.id ? ' home-product-featured' : ''}`} key={product.id} aria-labelledby={`${product.id}-heading`}>
                  <div className="home-product-top"><span className="home-product-role">{board.role}</span><span className="home-stock"><span aria-hidden="true" />{product.inStock ? 'In stock' : 'Out of stock'}</span></div>
                  <Link className="home-product-photo" to={`/product/${product.id}`} onClick={() => window.scrollTo(0, 0)}
                    aria-label={`View ${product.name} photos and specifications`} {...tracked('product_details', 'product_photo', product)}>
                    <OptimizedImage src={board.image} alt={`${product.name} hardware board`} width={board.width} height={board.height} />
                  </Link>
                  <div className="home-product-body">
                    <div className="home-product-title"><h3 id={`${product.id}-heading`}>{product.name}</h3><p className="home-price">{priceLabel(product)} <span>USD</span></p></div>
                    <p className="home-product-description">{board.description}</p>
                    <ul className="home-features">{board.features.map((feature) => <li key={feature}><Check size={17} aria-hidden="true" /><span>{feature}</span></li>)}</ul>
                    <p className="home-fit">{board.note}</p>
                    {product.id === 'logic-gates' && <a className="home-source-link" href="https://gitlab.com/oliver.vaht/logic-gates" target="_blank" rel="noopener noreferrer">Explore the open-source design files <ArrowUpRight size={14} aria-hidden="true" /></a>}
                    <div className="home-product-actions"><BuyButton product={product} placement="product_card" /><DetailLink product={product} placement="product_card" /></div>
                  </div>
                </article>
              );
            })}
          </div>
          <p className="home-order-note"><Package size={18} aria-hidden="true" /><span>Ships worldwide from Estonia. Check shipping costs and your order total on {storeName} before purchasing.</span></p>
          <p className="contact-purchase-note home-bulk-note">Bulk or classroom orders? <a href="#contact" className="contact-link" onClick={focusContact}>Contact us here</a>.</p>
        </section>

        <section className="home-learning-section" aria-labelledby="learning-heading">
          <div className="home-container home-section">
            <p className="home-eyebrow">Learn by connecting</p>
            <h2 id="learning-heading">From your first gate to a working circuit.</h2>
            <div className="home-benefits">
              <div><SlidersHorizontal size={24} aria-hidden="true" /><h3>Control the inputs</h3><p>Set HIGH and LOW with physical switches. See how the LED output changes and check it against the printed truth table.</p></div>
              <div><CircuitBoard size={24} aria-hidden="true" /><h3>Follow the signal</h3><p>On Logic Trainer, connect gates with jumper wires. Run a circuit with the adjustable clock or step through its states by hand.</p></div>
              <div><BookOpen size={24} aria-hidden="true" /><h3>Work at your own pace</h3><p>Use the free Logic Wiki alongside the hardware, with explanations and practical exercises you can return to as you learn.</p></div>
            </div>
            <div className="home-demo-layout">
              <div><p className="home-eyebrow">Interactive preview</p><h3>Try a gate.<br />Imagine a whole circuit.</h3><p>Select a gate and toggle its inputs. Then take the idea further on Logic Trainer: connect multiple gates, add a clock, and watch your circuit change with each step.</p><DetailLink product={trainer} placement="demo">See what Logic Trainer can do</DetailLink></div>
              <div className="home-demo"><GateDemo /></div>
            </div>
          </div>
        </section>

        <section className="home-section home-container" aria-labelledby="evidence-heading">
          <p className="home-eyebrow">A closer look before you buy</p>
          <h2 id="evidence-heading">Hardware you can investigate.</h2>
          <div className="home-evidence-grid">
            <div><h3>See it independently</h3><p>Watch EEVblog take a look at the original Logic Gates board, or read buyer reviews on its product listing.</p><a className="home-text-link" href="https://www.youtube.com/watch?v=CktljgyKILE&t=1035s" target="_blank" rel="noopener noreferrer">Watch Logic Gates on EEVblog <ArrowUpRight size={16} aria-hidden="true" /></a><a className="home-text-link" href={gates.buyLink} target="_blank" rel="noopener noreferrer" {...tracked('review_click', 'evidence', gates)}>Read Logic Gates reviews on {storeName} <ArrowUpRight size={16} aria-hidden="true" /></a></div>
            <div><h3>Inspect the design</h3><p>Logic Gates is OSHWA-certified open-source hardware. The design files are public if you want to study how the board is built.</p><a className="home-text-link" href="https://gitlab.com/oliver.vaht/logic-gates" target="_blank" rel="noopener noreferrer">Open the project files <ArrowUpRight size={16} aria-hidden="true" /></a></div>
            <div><h3>Know who makes it</h3><p>Designed, assembled and shipped from Estonia by Oliver. Board sales support further hardware development and free learning material.</p><Link className="home-text-link" to="/about" onClick={() => window.scrollTo(0, 0)}>Meet the maker <ArrowRight size={16} aria-hidden="true" /></Link></div>
          </div>
        </section>

        <section className="home-container home-faq-section" aria-labelledby="questions-heading">
          <div><p className="home-eyebrow">Before you order</p><h2 id="questions-heading">A few practical details.</h2></div>
          <div className="home-faq">
            <details><summary>Which board should I choose?</summary><p>Choose Logic Trainer to wire gates together and experiment with clocks, counters and memory. It works on its own; you do not need to buy Logic Gates first. Choose Logic Gates to focus on individual gates with switches and LEDs.</p></details>
            <details><summary>Do I need software or soldering equipment?</summary><p>Neither board requires software or soldering. Logic Gates runs on 2 × AAA batteries; Logic Trainer runs on 3 × AAA batteries and uses jumper wires to connect circuits.</p></details>
            <details><summary>Where do I buy, and how does shipping work?</summary><p>Orders are placed through {storeName}, a marketplace for independent hardware makers. Boards ship worldwide from Estonia. Review the listing and checkout for shipping to your destination and the final order total.</p></details>
            <details><summary>Can I order boards for a classroom or lab?</summary><p>For bulk or classroom orders, <a href="#contact" className="contact-link" onClick={focusContact}>contact us here</a>. Include the board, approximate quantity and delivery country in your message.</p></details>
            <details><summary>Can I read the learning material first?</summary><p>Yes. The <Link to="/logic-wiki" onClick={() => window.scrollTo(0, 0)} {...tracked('wiki_click', 'faq')}>Logic Wiki is free to read</Link>, including gate explanations, truth tables and practical exercises for the boards.</p></details>
          </div>
        </section>

        <section className="home-container home-closing" aria-label="Get Logic Trainer">
          <div><p className="home-eyebrow">Your next circuit starts here.</p><h2>Make room for Logic Trainer.</h2><p>16 gates, a clock, memory and a display. All on your desk.</p><Link className="home-text-link" to="/logic-wiki" onClick={() => window.scrollTo(0, 0)} {...tracked('wiki_click', 'closing')}>Start with the free Logic Wiki <ArrowRight size={17} aria-hidden="true" /></Link></div>
          <div className="home-closing-actions"><BuyButton product={trainer} placement="closing">Buy Logic Trainer · {priceLabel(trainer)}</BuyButton><span className="home-closing-note">USD · Checkout on {storeName}</span><a href="#products" className="home-text-link" {...tracked('compare_boards', 'closing')}>Compare the boards <ArrowRight size={17} aria-hidden="true" /></a></div>
        </section>
        <ContactSection />
      </main>
      <PcbFooter />
    </div>
  );
};

export default LandingPage;
